/**
 * Recursos de IA no detalhe do ticket (v11): "Sugerir resposta" (preenche o
 * composer da Comunicação com o texto retornado, editável antes do envio) e
 * "Resumir com IA" (dialog com o resumo). Ambos só aparecem quando GET
 * /ai/status diz active (query pequena, staleTime 5min — atendentes não leem
 * a config, só o flag). Erros do provedor via transient-toast / inline.
 */
import { useEffect, useState } from 'react';
import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { AI_ERROR_LABELS, AI_REQUEST_TIMEOUT_MS, type AiStatus, type AiText } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Dialog, Spinner } from '@/components/ui/misc';
import { useTransientToast } from '@/components/ui/transient-toast';
import { errorMessage as genericErrorMessage } from './ticket-format';

/**
 * Orçamento do cliente para as rotas de IA: o servidor espera a Anthropic por
 * AI_REQUEST_TIMEOUT_MS — igual ao timeout padrão do api(), que abortava do
 * lado do cliente conclusões perto do limite. Orçamento do servidor + folga
 * de rede para a resposta chegar.
 */
const AI_CLIENT_TIMEOUT_MS = AI_REQUEST_TIMEOUT_MS + 15_000;

/** Rótulos de IA em camada sobre o fallback genérico (padrão chats-format). */
function aiErrorMessage(e: unknown): string {
  if (e !== null && typeof e === 'object' && 'error' in e) {
    const code = (e as { error: unknown }).error;
    if (typeof code === 'string') {
      const label = AI_ERROR_LABELS[code];
      if (label) return label;
    }
  }
  return genericErrorMessage(e);
}

/** Flag global "IA ativa" — visível a qualquer staff (a config é admin-only). */
export function aiStatusQuery() {
  return queryOptions({
    queryKey: ['erp', 'ai', 'status'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<AiStatus>('/ai/status', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
    staleTime: 5 * 60_000,
  });
}

/** `true` quando os recursos de IA devem aparecer (erro/carregando = esconde). */
export function useAiActive(): boolean {
  const statusQ = useQuery(aiStatusQuery());
  return statusQ.data?.active === true;
}

/**
 * Botão "Sugerir resposta" do composer da Comunicação — POST
 * /tickets/:n/ai/suggest e entrega o {text} ao composer (que decide como
 * inserir). Renderize apenas quando useAiActive().
 */
export function AiSuggestButton({
  ticketNumber,
  disabled,
  onSuggested,
}: {
  ticketNumber: number;
  disabled?: boolean;
  onSuggested: (text: string) => void;
}) {
  const { toast, showToast } = useTransientToast('error');

  const suggest = useMutation({
    mutationFn: async () => {
      const res = await api<AiText>(`/tickets/${ticketNumber}/ai/suggest`, {
        method: 'POST',
        timeoutMs: AI_CLIENT_TIMEOUT_MS,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (data) => onSuggested(data.text),
    onError: (e) => showToast(aiErrorMessage(e)),
  });

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled || suggest.isPending}
        title="Sugerir resposta com IA"
        onClick={() => suggest.mutate()}
      >
        {suggest.isPending ? <Spinner className="size-4" /> : <Sparkles />}
        Sugerir resposta
      </Button>
      {toast}
    </>
  );
}

/**
 * Dialog "Resumir com IA" — dispara o POST /tickets/:n/ai/summary ao abrir e
 * mostra o resumo (spinner enquanto gera; erro com tentar novamente).
 */
export function AiSummaryDialog({
  ticketNumber,
  onClose,
}: {
  ticketNumber: number;
  onClose: () => void;
}) {
  const summary = useMutation({
    mutationFn: async () => {
      const res = await api<AiText>(`/tickets/${ticketNumber}/ai/summary`, {
        method: 'POST',
        timeoutMs: AI_CLIENT_TIMEOUT_MS,
      });
      if (res.error) throw res.error;
      return res.data;
    },
  });

  // Uma geração por abertura do dialog (StrictMode-safe via flag de estado).
  const [started, setStarted] = useState(false);
  useEffect(() => {
    if (started) return;
    setStarted(true);
    summary.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started]);

  return (
    <Dialog open onClose={onClose} title={`Resumo do ticket #${ticketNumber}`}>
      <div className="space-y-3">
        {summary.data === undefined && !summary.isError ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Spinner className="size-5" />
            Gerando resumo…
          </div>
        ) : summary.isError ? (
          <div className="space-y-3 py-2">
            <p className="text-sm text-destructive" role="alert">
              {aiErrorMessage(summary.error)}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => summary.mutate()}
            >
              Tentar novamente
            </Button>
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto rounded-md border bg-muted/30 p-3 text-sm leading-relaxed whitespace-pre-wrap">
            {summary.data.text}
          </div>
        )}
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Sparkles className="size-3.5 shrink-0" />
          Resumo gerado por IA — confira antes de usar.
        </p>
        <div className="flex justify-end">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
